import React, { useCallback, useEffect, useState } from 'react';
import { getSavedCreditCards } from '../../../data/data-service';
import Preloader from '../../../../../../ui/svg/common/preloader';
import { ICreditCard } from '../../../interfaces';
import { Popup } from '../../../../../../ui/popup/popup';
import { rootActions, savedCreditCardActions, useAppDispatch, useAppSelector } from '../../../data/store';
import { CreditCardTabTabsEnum } from '../../../enums/credit-card-tab';
import { checkIsSubscription } from '../../../domain/shopping-cart-provider';
import SavedCreditCard from './saved-credit-card';

export const UPDATE_SAVED_CREDIT_CARDS_EVENT = 'UPDATE_SAVED_CARDS_EVT';

const SavedCreditCards = () => {

    const dispatch = useAppDispatch();

    const savedCreditCards = useAppSelector(store => store.savedCreditCard.savedCreditCards);
    const cartData = useAppSelector(store => store.root.cartData);
    const inUsePopupShown = useAppSelector(store => store.savedCreditCard.inUsePopupShown);

    const isSubscription = checkIsSubscription(cartData);
    const creditCards = isSubscription ?
        savedCreditCards.filter(card => card.cardType !== 'DEBIT' && card.cardType !== 'PREPAID') :
        savedCreditCards;

    const [isLoading, setIsLoading] = useState(false);

    /**
     * update saved cards
     */
    const updateSavedCards = useCallback((_savedCards: ICreditCard[]) => {

        dispatch(
            savedCreditCardActions.main({
                savedCreditCards: _savedCards,
            })
        );

        if(_savedCards.length <= 0){

            // jump to 'new credit card' tab
            dispatch(
                rootActions.main({
                    selectedCreditCardTab: CreditCardTabTabsEnum.NewCreditCard,
                })
            );
            return;
        }

        dispatch(
            savedCreditCardActions.main({
                savedCreditCardToken: _savedCards[0].token,
                networkTransactionID: _savedCards[0].networkTransactionID || '',
                isRecurringNetworkTransactionID: _savedCards[0].isRecurring,
            })
        );
    }, [ dispatch ]);

    /**
     * Update saved credit cards list from outside
     */
    useEffect(() => {

        const updateSavedCardsFromOutside = (evt: Event) => {

            const detail = (evt as CustomEvent)?.detail;
            if(!detail) return;

            updateSavedCards(detail);
        };

        document.addEventListener(UPDATE_SAVED_CREDIT_CARDS_EVENT, updateSavedCardsFromOutside);

        return () => {
            document.removeEventListener(UPDATE_SAVED_CREDIT_CARDS_EVENT, updateSavedCardsFromOutside);
        };
    }, [updateSavedCards]);

    /**
     * load saved cards data on component init
     */
    useEffect(() => {
        (async () => {
            setIsLoading(true);

            try{
                const response = await getSavedCreditCards();
                if(!response || response.statusCode !== 200 || !response.data.isValid || !response.data.relatedData){
                    setIsLoading(false);
                    updateSavedCards([]);
                    return;
                }

                const _savedCards = response.data.relatedData as ICreditCard[];
                updateSavedCards(_savedCards);
            }
            catch(ex){}

            setIsLoading(false);
        })();
    }, [ updateSavedCards ]);

    /**
     * on 'Add New Card' button click
     */
    const handleAddNewCardButton = () => {

        dispatch(
            rootActions.main({
                selectedCreditCardTab: CreditCardTabTabsEnum.NewCreditCard,
            })
        );
    };

    const hidePopup = () => {
      dispatch(
          savedCreditCardActions.main({
              inUsePopupShown: false,
          })
      )
    };

    return (
        <div>
            {
                isLoading &&
                <div className="flex justify-center">
                    <Preloader width="25px" height="25px" color="#4f4f4f" />
                </div>
            }

            {
                !isLoading && savedCreditCards &&
                <>
                    <button
                        type="button"
                        data-qa="cart-step3-add-new-card-link"
                        className="text-tiny font-bold text-blue-200 mb-4"
                        onClick={ handleAddNewCardButton }>
                        + Add New Card
                    </button>

                    {
                        creditCards.map(savedCard => {

                            return (
                                <SavedCreditCard
                                    key={ savedCard.token }
                                    savedCard={ savedCard }
                                />
                            )
                        })
                    }
                </>
            }

            {
                inUsePopupShown &&
                <Popup width="290px" height="200px" close={ hidePopup }>
                    <div className="flex flex-col justify-center items-center w-full h-full text-center">
                        <p className="mb-4">Your credit card can't be removed, it's used by your Waves subscription.</p>
                        <button className="w-btn" type="button" onClick={ hidePopup }>OK</button>
                    </div>
                </Popup>
            }
        </div>
    )
};

export default SavedCreditCards;